"use client";

import { useState, useCallback } from "react";
import { ArrowRight, Trash2 } from "lucide-react";
import { Input } from "@stockline/ui/components/input";
import { useProposalMutations } from "@stockline/web";
import { useTranslation } from "react-i18next";
import type { Proposal } from "@stockline/shared/types/ai/proposal";
import {
  getDisplayFields,
  getFallbackFields,
  payloadToFields,
  type ProposalCardInnerProps,
  type DisplayField,
} from "./proposal-card-shared";
import { ProposalCardShell } from "./ProposalCardShell";

interface EditingState {
  proposalId: string;
  key: string;
  value: string;
}

export function ProposalGenericCard(props: ProposalCardInnerProps) {
  const { batch } = props;
  const { t } = useTranslation();
  const { editProposal, removeProposal } = useProposalMutations();
  const [editing, setEditing] = useState<EditingState | null>(null);

  const isPending = batch.status === "pending";

  const displayFields = getDisplayFields(batch, t);
  const fields =
    displayFields.length > 0 ? displayFields : getFallbackFields(batch, t);

  const handleFieldClick = useCallback(
    (proposal: Proposal, key: string, currentValue: string) => {
      if (!isPending) return;
      setEditing({ proposalId: proposal.id, key, value: currentValue });
    },
    [isPending],
  );

  const handleFieldSave = useCallback(async () => {
    if (!editing) return;
    const inFlight = editing;
    const originalValue = batch.proposals.find(
      (p) => p.id === inFlight.proposalId,
    )?.payload?.[inFlight.key];
    let value: unknown;
    if (typeof originalValue === "number") {
      const parsed = Number(inFlight.value);
      value = Number.isFinite(parsed) ? parsed : originalValue;
    } else if (typeof originalValue === "boolean") {
      const lowered = inFlight.value.trim().toLowerCase();
      if (lowered === "true") value = true;
      else if (lowered === "false") value = false;
      else value = originalValue;
    } else {
      value = inFlight.value;
    }
    await editProposal(batch.id, inFlight.proposalId, {
      [inFlight.key]: value,
    });
    // Avoid clobbering a newer edit started while the save was in flight.
    setEditing((curr) => (curr === inFlight ? null : curr));
  }, [editing, batch.id, batch.proposals, editProposal]);

  const handleFieldCancel = useCallback(() => {
    setEditing(null);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleFieldSave().catch(console.error);
      } else if (e.key === "Escape") {
        handleFieldCancel();
      }
    },
    [handleFieldSave, handleFieldCancel],
  );

  const handleRemoveProposal = useCallback(
    (proposalId: string) => {
      removeProposal(batch.id, proposalId).catch(console.error);
    },
    [batch.id, removeProposal],
  );

  const proposalFieldGroups = batch.proposals.map((p) => ({
    proposal: p,
    fields: payloadToFields(p.payload ?? {}),
  }));

  const useDisplayFields = displayFields.length > 0;

  return (
    <ProposalCardShell {...props}>
      {/* Fields — display_fields mode (read-only summary) */}
      {useDisplayFields && fields.length > 0 && (
        <div className="px-3 pb-2">
          <div className="rounded-md bg-background/60 border border-border/50 divide-y divide-border/30">
            {fields.map((field) => (
              <FieldRow key={field.label} field={field} />
            ))}
          </div>
        </div>
      )}

      {/* Fields — per-proposal editable mode */}
      {!useDisplayFields &&
        proposalFieldGroups.map(({ proposal, fields: pFields }) => (
          <div
            key={proposal.id}
            className="px-3 pb-2"
            data-testid={`proposal-group-${proposal.id}`}
          >
            <div className="rounded-md bg-background/60 border border-border/50 divide-y divide-border/30">
              {pFields.map((field) => {
                const isEditing =
                  editing?.proposalId === proposal.id &&
                  editing?.key === field.payloadKey;

                return (
                  <div
                    key={field.label}
                    className="flex items-center gap-3 px-3 py-1.5 text-sm group"
                    data-testid={`proposal-field-${field.payloadKey}`}
                  >
                    <span className="text-muted-foreground text-xs min-w-[100px] shrink-0">
                      {field.label}
                    </span>
                    <GenericFieldValue
                      field={field}
                      isEditing={isEditing}
                      isPending={isPending}
                      editing={editing}
                      onEditingChange={setEditing}
                      onKeyDown={handleKeyDown}
                      onSave={handleFieldSave}
                      onClick={() =>
                        field.payloadKey &&
                        handleFieldClick(
                          proposal,
                          field.payloadKey,
                          field.value,
                        )
                      }
                    />
                    {isPending && !isEditing && batch.proposals.length > 1 && (
                      <button
                        onClick={() => handleRemoveProposal(proposal.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                        title={t("aiProposals.removeProposal")}
                        data-testid={`proposal-remove-${proposal.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
    </ProposalCardShell>
  );
}

function GenericFieldValue({
  field,
  isEditing,
  isPending,
  editing,
  onEditingChange,
  onKeyDown,
  onSave,
  onClick,
}: {
  readonly field: DisplayField;
  readonly isEditing: boolean;
  readonly isPending: boolean;
  readonly editing: EditingState | null;
  readonly onEditingChange: (e: EditingState) => void;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
  readonly onSave: () => Promise<void>;
  readonly onClick: () => void;
}) {
  if (isEditing && editing) {
    return (
      <div className="flex items-center gap-1 flex-1">
        <Input
          value={editing.value}
          onChange={(e) =>
            onEditingChange({ ...editing, value: e.target.value })
          }
          onKeyDown={onKeyDown}
          onBlur={() => onSave().catch(console.error)}
          className="h-6 text-sm px-1.5"
          data-testid={`proposal-edit-input-${field.payloadKey}`}
          autoFocus
        />
      </div>
    );
  }

  if (isPending && field.payloadKey) {
    return (
      <button
        type="button"
        className="font-medium flex-1 text-left cursor-pointer hover:text-primary bg-transparent border-0 p-0"
        onClick={onClick}
        data-testid={`proposal-field-value-${field.payloadKey}`}
      >
        {field.value}
      </button>
    );
  }

  return (
    <span
      className="font-medium flex-1"
      data-testid={`proposal-field-value-${field.payloadKey}`}
    >
      {field.value}
    </span>
  );
}

function FieldRow({ field }: { readonly field: DisplayField }) {
  return (
    <div className="flex items-baseline gap-3 px-3 py-1.5 text-sm">
      <span className="text-muted-foreground text-xs min-w-[100px] shrink-0">
        {field.label}
      </span>
      {field.oldValue ? (
        <span className="flex items-center gap-1.5">
          <span className="text-muted-foreground line-through">
            {field.oldValue}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="font-medium">{field.value}</span>
        </span>
      ) : (
        <span className="font-medium">{field.value}</span>
      )}
    </div>
  );
}
